import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Table } from "react-bootstrap";
import "../License/License.css";
import "../../Dashboard/Dashboard.css";
import "../../../../../common/styles/table.css";
import TablePagination from "../../../TeacherAdmin/pagination/TablePagination";
import { useGetAllPayments } from "../../../../../hooks/school/payments/useGetAllPayments";
import TableSkeleton from "../../../common/TableSkeleton";
import { ITEMS_PER_PAGE } from "../../../../../constant/constant";

/**
 * @typedef {"school" | "organization"} tenanttype
 */

/**
 * Payment component - Invoices & Payments dashboard
 * @param {{ type?: tenanttype }} props
 */
const Payment = ({ type = "school" }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { payments, meta, loading, error, getPayments } = useGetAllPayments();

  const getStatusConfig = (status) => {
    switch (status) {
      case "paid":
        return { label: "Paid", className: "badge bg-success" };

      case "failed":
        return { label: "Failed", className: "badge bg-danger" };

      case "pending":
      case "partial":
      default:
        return { label: "Pending", className: "badge bg-warning text-dark" };
    }
  };

  // Fetch payments
  useEffect(() => {
    getPayments({
      page: currentPage,
      per_page: 10,
      sort: "-created_at",
    });
  }, [currentPage]);

  /**
   * API → UI mapping (NO DESIGN CHANGE)
   */
  const formatPaymentType = (value = "") =>
    value
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const formattedPayments = useMemo(() => {
    if (!payments) return [];

    return payments.map((item) => ({
      id: item.id,
      date: new Date(item?.created_at).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      invoice_id: item?.invoice?.id,
      paymentFor: `${item?.invoice?.invoice_no}`,
      amount: `${Number(item?.amount).toLocaleString("en-IN")}`,
      paymentType: item?.method ? formatPaymentType(item.method) : "-",
      status: item?.status,
      invoicePdf: item?.invoice_pdf_path,
      currency: item?.currency,
    }));
  }, [payments]);

  const handleViewReceipt = (pdfPath) => {
    if (!pdfPath) return;
    window.open(pdfPath, "_blank");
  };

  if (loading && payments === null) {
    return (
      <div>
        <div className="license-header">
          <h1 className="license-title">Payments</h1>
        </div>
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />
      </div>
    );
  }

  return (
    <div className="">
      <div className="">
        {/* Header */}
        <div className="license-header">
          <h1 className="license-title">Payments</h1>
        </div>

        {/* Loading */}
        {loading && <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />}

        {/* Error */}
        {error && <div className="text-center text-danger my-4">{error}</div>}

        {/* Payments Table */}
        {!loading && !error && (
          <div className="custom-table-container mt-3">
            <div className="custom-table-wrapper">
              <Table hover className="custom-table">
                <thead className="custom-table-head">
                  <tr>
                    <th>Date</th>
                    {/* <th>Payment For</th> */}
                    <th>Amount</th>
                    <th>Payment Type</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formattedPayments.length > 0 ? (
                    formattedPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{payment.date}</td>
                        {/* <td>{payment.paymentFor}</td> */}
                        <td>
                          {payment.amount} ({payment.currency})
                        </td>
                        <td>{payment.paymentType}</td>
                        <td>
                          {(() => {
                            const status = getStatusConfig(payment.status);
                            return (
                              <span className={status.className + " px-3 py-2"}>
                                {status.label}
                              </span>
                            );
                          })()}
                        </td>

                        <td>
                          <Link
                            to={
                              type === "school"
                                ? `/school-admin/view/payment/${payment.id}`
                                : `/organization/view/payment/${payment.id}`
                            }
                            className="view-receipt-link"
                            // onClick={() =>
                            //   handleViewReceipt(payment.invoicePdf)
                            // }
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <div className="text-center m-5">
                          No payments found.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <TablePagination
              totalPage={meta?.last_page || 1}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
